import i18n from '../../../i18n.js';

export function fetchAndRenderChart(data = [], capitalTarget = 0, yearlyDetails = {}) {
  try {
    console.log("DATA", data);
    console.log("YEARLY DETAILS", yearlyDetails);

    if (data.length === 0) {
      clearChart();
      return;
    }

    const years = data.map(item => item.year);
    const callsData = data.map(item => item.call || 0);
    const distsData = data.map(item => item.dist || 0);
    const netData = data.map(item => item.cf || 0);

    console.log("YEARS", years);

    let cumul = 0;
    const fluxPrevisionnels = netData.map(v => {
      cumul += v;
      return cumul;
    });

    // CALCUL DYNAMIQUE DES ÉCHELLES
    const allValues = [...callsData, ...distsData, ...netData, ...fluxPrevisionnels];

    if (capitalTarget > 0) {
      allValues.push(-capitalTarget);
    }
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);

    // Fonction d'arrondi pour le maximum
    const smartRoundUp = (value) => {
      if (value <= 0) return 0;

      const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
      const normalized = value / magnitude;

      let roundedNormalized;
      if (normalized <= 1) roundedNormalized = 1;
      else if (normalized <= 2) roundedNormalized = 2;
      else if (normalized <= 5) roundedNormalized = 5;
      else roundedNormalized = 10;

      return roundedNormalized * magnitude;
    };

    // Fonction d'arrondi pour le minimum (vers le bas)
    const smartRoundDown = (value) => {
      if (value >= 0) return 0;

      const magnitude = Math.pow(10, Math.floor(Math.log10(Math.abs(value))));
      const normalized = Math.abs(value) / magnitude;

      let roundedNormalized;
      if (normalized <= 1) roundedNormalized = 1;
      else if (normalized <= 2) roundedNormalized = 2;
      else if (normalized <= 5) roundedNormalized = 5;
      else roundedNormalized = 10;

      return -roundedNormalized * magnitude;
    };

    const roundedMin = smartRoundDown(minValue);
    const roundedMax = smartRoundUp(maxValue);

    // Formatter les valeurs pour l'affichage
    const formatValue = (value) => {
      if (Math.abs(value) >= 1000000) {
        return (value / 1000000).toFixed(1) + 'M€';
      } else if (Math.abs(value) >= 1000) {
        return (value / 1000).toFixed(0) + 'k€';
      } else {
        return value + '€';
      }
    };

    // Fonction pour formater les détails du tooltip
    const formatTooltipDetails = (year) => {
      const details = yearlyDetails[year];
      if (!details || details.length === 0) {
        return '';
      }

      let tooltipContent = '<br/><br/>';

      // Grouper par fonds
      const groupedByFund = details.reduce((acc, detail) => {
        if (!acc[detail.fund_name]) {
          acc[detail.fund_name] = [];
        }
        acc[detail.fund_name].push(detail);
        return acc;
      }, {});

      Object.keys(groupedByFund).forEach(fundName => {
        tooltipContent += `<strong>${fundName} :</strong><br/>`;

        // Dédupliquer les cashflows par date et pourcentage pour éviter les doublons
        const uniqueCashflows = [];
        const seenCashflows = new Set();

        groupedByFund[fundName].forEach(detail => {
          const key = `${detail.date}-${detail.percentage}`;
          if (!seenCashflows.has(key)) {
            seenCashflows.add(key);
            uniqueCashflows.push(detail);
          }
        });

        // Afficher chaque cashflow unique séparément
        uniqueCashflows.forEach(detail => {
          const percentageValue = Math.abs(detail.percentage * 100).toFixed(2);
          const type = detail.percentage < 0 ? i18n.t("app.charts.legends.capital_calls") : i18n.t("app.charts.legends.distributions");

          tooltipContent += `${type} ${percentageValue}%<br/>`;
        });

        tooltipContent += '<br/>';
      });

      return tooltipContent;
    };

    const option = {
      toolbox: {
        show: true,
        right: '5%',
        top: '5%',
        feature: {
          dataView: { show: true, readOnly: false },
          magicType: { show: true, type: ['line', 'bar'] },
          restore: { show: true },
          saveAsImage: { show: true }
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#6a7985'
          }
        },
        formatter: function(params) {
          let result = `<strong>${i18n.t('app.charts.tooltip.year')} ${params[0].name}</strong><br/>`;

          const yearIndex = years.indexOf(parseInt(params[0].name));
          const callVal = callsData[yearIndex] || 0;
          const distVal = distsData[yearIndex] || 0;
          const netVal = netData[yearIndex] || 0;

          let fluxConsolides = 0;
          params.forEach(param => {
            if (param.seriesName === i18n.t("app.charts.legends.consolidated_cashflows_forecasts")) {
              fluxConsolides = param.value;
            }
          });

          const callMarker = `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:9px;height:9px;background-color:#FF4654"></span>`;
          const distMarker = `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:9px;height:9px;background-color:#82969c"></span>`;
          const netMarker = `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:9px;height:9px;background-color:#003e6f"></span>`;
          const fluxMarker = `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:9px;height:9px;background-color:#8B5A96"></span>`;

          result += `${callMarker}${i18n.t('app.charts.legends.capital_calls')}: ${formatValue(callVal)}<br/>`;
          result += `${distMarker}${i18n.t('app.charts.legends.distributions')}: ${formatValue(distVal)}<br/>`;
          result += `${netMarker}${i18n.t('app.charts.tooltip.net_cashflow')}: ${formatValue(netVal)}<br/>`;
          result += `${fluxMarker}${i18n.t('app.charts.tooltip.consolidated_cashflow')}: ${formatValue(fluxConsolides)}<br/>`;

          const yearDetails = formatTooltipDetails(parseInt(params[0].name));
          result += yearDetails;

          return result;
        }
      },
      legend: {
        data: [ 
          i18n.t("app.charts.legends.capital_calls"),
          i18n.t("app.charts.legends.distributions"),
          i18n.t("app.charts.legends.net_cashflow"),
          i18n.t("app.charts.legends.consolidated_cashflows_forecasts")
        ],
        bottom: 10,
        orient: 'horizontal'
      },
      xAxis: {
        type: 'category',
        data: years,
        axisPointer: { type: 'shadow' }
      },
      yAxis: {
        type: 'value',
        name: i18n.t('app.charts.amount'),
        min: roundedMin,
        max: roundedMax,
        axisLabel: {
          formatter: function(value) {
            return formatValue(value);
          }
        }
      },
      series: [
        {
          name: i18n.t("app.charts.legends.capital_calls"),
          type: 'bar',
          barWidth: 18,
          itemStyle: { color: '#FF4654' },
          data: callsData,
          barGap: '30%',
          markLine: capitalTarget > 0 ? {
            silent: true,
            symbol: ['none', 'none'],
            label: { show: false },
            lineStyle: {
              color: 'rgb(45,135,0)',
              type: 'solid',
              width: 2
            },
            data: [{ yAxis: -capitalTarget }]
          } : undefined
        },
        {
          name: i18n.t("app.charts.legends.distributions"),
          type: 'bar',
          barWidth: 18,
          itemStyle: { color: '#82969c' },
          data: distsData
        },
        {
          name: i18n.t("app.charts.legends.consolidated_cashflows_forecasts"),
          type: 'line',
          data: fluxPrevisionnels,
          symbol: 'none',
          smooth: true,
          color: '#8B5A96',
          lineStyle: {
            color: '#8B5A96',
            type: 'solid',
            width: 3
          }
        },
        {
          name: i18n.t("app.charts.legends.net_cashflow"),
          type: 'scatter',
          data: netData,
          symbol: 'diamond',
          symbolSize: 12,
          color: '#003e6f'
        }
      ]
    };

    const chartDom = document.getElementById('cf_echarts');
    const existingChart = echarts.getInstanceByDom(chartDom);
    if (existingChart) {
      existingChart.dispose();
    }

    const chart = echarts.init(chartDom, null, { renderer: 'canvas' });

    //console.log("I18n:", i18n.t("app.charts.legends.capital_calls"));
    //console.log("options:", option);

    chart.setOption(option);

  } catch (error) {
    console.error('Erreur lors du chargement du JSON :', error);
    clearChart();
  }
}

function clearChart() {
  const chartDom = document.getElementById("cf_echarts");
  if (chartDom) {
    const chart = echarts.getInstanceByDom(chartDom);
    if (chart) {
      chart.dispose();
    }
  }
}